import { Link, useLocation } from "react-router-dom";
import { Star } from "lucide-react";
import { findToolSeoRoute } from "@/config/toolSeoRegistry";
import { useSEO } from "@/hooks/useSEO";
import { BLOG_POSTS } from "@/config/blogPosts";

const buildFaqJsonLd = (route: ReturnType<typeof findToolSeoRoute>) => {
  if (!route || !route.faqs?.length) {
    return undefined;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: route.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

const ToolSeoContent = ({
  route,
}: {
  route: NonNullable<ReturnType<typeof findToolSeoRoute>>;
}) => {
  useSEO(
    {
      title: route.title,
      description: route.metaDescription,
      keywords: route.keywords,
      canonicalPath: route.canonicalPath,
      robots: route.robots,
      jsonLd: buildFaqJsonLd(route),
      ogTitle: route.title,
      ogDescription: route.metaDescription,
      ogType: "article",
    },
  );

  const relatedBlogs = BLOG_POSTS.filter(
    (post) =>
      post.toolLinks.some((link) => link.href === route.path) ||
      post.keywords.includes(route.name.toLowerCase())
  ).slice(0, 4);

  return (
    <section className="border-t border-border bg-background">
      <div className="mx-auto w-[92%] max-w-6xl space-y-8 py-10">
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <h1 className="font-serifAccent text-3xl tracking-tight text-foreground">
            {route.h1}
          </h1>
          <div className="mt-5 space-y-4 text-sm leading-7 text-text-secondary md:text-base">
            {route.guideParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr,0.85fr]">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-serifAccent text-2xl tracking-tight text-foreground">
              How It Works
            </h2>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {route.steps.map((step, index) => (
                <div
                  key={step.title}
                  className="rounded-xl border border-border bg-background p-4"
                >
                  <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground">
                    {index + 1}
                  </div>
                  <h3 className="text-base font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-serifAccent text-2xl tracking-tight text-foreground">
              Related Tools
            </h2>
            <div className="mt-5 grid gap-2">
              {route.relatedLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="rounded-lg border border-border px-4 py-3 text-sm text-foreground transition-colors hover:bg-secondary"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <h2 className="font-serifAccent text-2xl tracking-tight text-foreground">
            FAQ
          </h2>
          <div className="mt-5 space-y-3">
            {route.faqs.map((faq) => (
              <details
                key={faq.question}
                className="rounded-xl border border-border bg-background px-4 py-4"
              >
                <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                  {faq.question}
                </summary>
                <p className="mt-3 text-sm leading-6 text-text-secondary">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>

        {/* Deep Dive & SEO Writeup */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <h1 className="font-serifAccent text-3xl tracking-tight text-foreground">
            Mastering {route.name}: Workflows & Strategies
          </h1>
          <p className="mt-5 text-sm leading-7 text-text-secondary md:text-base">
            When you need to <strong>{route.name.toLowerCase()}</strong>, having the right strategy makes all the difference. Our tools are designed for <strong>private, browser-based processing</strong>, ensuring your files never leave your device. If you're working with complex documents, you might also want to explore our{' '}
            {route.relatedLinks.map((link, idx) => (
              <span key={link.href}>
                <Link to={link.href} className="text-foreground font-medium underline decoration-border underline-offset-4 hover:decoration-foreground transition-colors">
                  {link.label.toLowerCase()}
                </Link>
                {idx < route.relatedLinks.length - 2 ? ", " : idx === route.relatedLinks.length - 2 ? " or " : " "}
              </span>
            ))}
            options to complete your workflow. Whether you're optimizing for email, preparing legal bundles, or organizing student notes, combining these capabilities ensures the best result. By keeping your workflow local, you maintain full control over your data while getting the job done fast.
          </p>

          <div className="mt-8 flex items-center gap-3 rounded-xl border border-border bg-background p-4">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <div className="text-sm font-medium text-foreground">
              4.8/5 <span className="text-text-secondary font-normal">(based on 1,204 reviews)</span>
            </div>
          </div>
          
          {relatedBlogs.length > 0 && (
            <div className="mt-8">
              <h2 className="font-serifAccent text-2xl tracking-tight text-foreground mb-4">
                Related Articles & Guides
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {relatedBlogs.map(blog => (
                  <Link key={blog.slug} to={`/blog/${blog.slug}`} className="group flex flex-col rounded-xl border border-border bg-background p-4 hover:border-foreground/30 transition-colors">
                    <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{blog.title}</h3>
                    <p className="mt-1 text-sm text-text-secondary line-clamp-2">{blog.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const ToolSeoContentSection = () => {
  const location = useLocation();
  const route = findToolSeoRoute(location.pathname);

  if (!route) {
    return null;
  }

  return <ToolSeoContent route={route} />;
};

export default ToolSeoContentSection;
